import React from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Divider, Form, Modal } from 'antd';

import BaseComponent from 'base/BaseComponent';

import { formatAgreementName, getAgreementKey, renderAgreementContent } from './agreementUtil';
import './FhAgreementConsent.less';

const FETCH_URL = '/system/agreement/getActiveAgreementList';
const ACCEPT_URL = '/system/agreement/acceptActiveAgreementList';

/**
 * 协议阅读与同意
 * mode=form：嵌入 Form，用于注册等表单场景
 * mode=blocking：登录后全屏遮罩弹窗，须勾选同意后方可继续
 */
class FhAgreementConsent extends BaseComponent {
  static propTypes = {
    mode: PropTypes.oneOf(['form', 'blocking']),
    showDivider: PropTypes.bool,
    autoFetch: PropTypes.bool,
    formItemName: PropTypes.string,
    formRef: PropTypes.object,
    fetchUrl: PropTypes.string,
    acceptUrl: PropTypes.string,
    checkLogin: PropTypes.bool,
    onConfirmed: PropTypes.func,
    onEmpty: PropTypes.func,
    onLoaded: PropTypes.func
  };

  static defaultProps = {
    mode: 'form',
    showDivider: true,
    autoFetch: true,
    formItemName: 'agreementAccepted',
    fetchUrl: FETCH_URL,
    acceptUrl: ACCEPT_URL,
    checkLogin: true
  };

  constructor(props) {
    super(props, props.checkLogin);
    this.state = {
      agreementList: [],
      viewingAgreement: null,
      blockingVisible: false,
      accepted: false,
      submitting: false
    };
  }

  componentDidMount() {
    if (this.props.autoFetch) {
      this.fetchAgreementList();
    }
  }

  fetchAgreementList = () => {
    this.sendPostRequest(this.props.fetchUrl, {}, (result) => {
      const agreementList = result || [];
      this.setState({ agreementList }, () => {
        if (!agreementList.length) {
          this.props.onEmpty && this.props.onEmpty();
          return;
        }
        if (this.props.mode === 'blocking') {
          this.setState({ blockingVisible: true, accepted: false });
        }
        this.props.onLoaded && this.props.onLoaded(agreementList);
      });
    });
  }

  resetAccepted = () => {
    this.setState({ accepted: false });
    const form = this.props.formRef && this.props.formRef.current;
    if (form) {
      form.setFieldsValue({ [this.props.formItemName]: false });
    }
  }

  getAgreementIdList = () => {
    const { agreementList } = this.state;
    if (!agreementList || !agreementList.length) {
      return [];
    }
    return agreementList
      .map((item) => item.agreementId);
  }

  showAgreementDetail = (agreement) => {
    this.setState({ viewingAgreement: agreement });
  }

  hideAgreementDetail = () => {
    this.setState({ viewingAgreement: null });
  }

  handleBlockingConfirm = () => {
    if (!this.state.accepted) {
      return;
    }
    const finish = () => {
      this.setState({ submitting: false, blockingVisible: false });
      this.props.onConfirmed && this.props.onConfirmed();
    };
    this.setState({ submitting: true });
    if (this.props.acceptUrl) {
      this.sendPostRequest(this.props.acceptUrl, {
        idList: this.getAgreementIdList()
      }, finish, () => {
        this.setState({ submitting: false });
      });
    } else {
      finish();
    }
  }

  renderAgreementLinks = () => {
    const { agreementList } = this.state;
    return agreementList.map((item, index) => (
      <React.Fragment key={getAgreementKey(item, index)}>
        {index > 0 && <span className="fh-agreement-consent-text">、</span>}
        <a
          className="fh-agreement-consent-link"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            this.showAgreementDetail(item);
          }}
        >
          {formatAgreementName(item.typeName)}
        </a>
      </React.Fragment>
    ));
  }

  renderDetailModal = () => {
    const { viewingAgreement } = this.state;
    return (
      <Modal
        className="fh-agreement-modal"
        title={viewingAgreement ? formatAgreementName(viewingAgreement.typeName) : ''}
        visible={!!viewingAgreement}
        onCancel={this.hideAgreementDetail}
        width={720}
        footer={[<Button key="close" type="primary" onClick={this.hideAgreementDetail}>我知道了</Button>]}
        destroyOnClose
      >
        {viewingAgreement && renderAgreementContent(viewingAgreement.content)}
      </Modal>
    );
  }

  renderFormMode = () => {
    const { agreementList } = this.state;
    const { showDivider, formItemName } = this.props;
    if (!agreementList.length) {
      return null;
    }
    return (
      <React.Fragment>
        {showDivider && <Divider dashed />}
        <Form.Item
          name={formItemName}
          valuePropName="checked"
          className="fh-agreement-consent-item"
          wrapperCol={{ span: 24 }}
          rules={[{
            validator: (_, value) => (value
              ? Promise.resolve()
              : Promise.reject(new Error('请先阅读并同意相关协议')))
          }]}
        >
          <Checkbox>
            <span className="fh-agreement-consent-text">我已阅读并同意</span>
            {this.renderAgreementLinks()}
          </Checkbox>
        </Form.Item>
        {this.renderDetailModal()}
      </React.Fragment>
    );
  }

  renderBlockingMode = () => {
    const { agreementList, blockingVisible, accepted, submitting } = this.state;
    if (!agreementList.length) {
      return null;
    }
    return (
      <React.Fragment>
        <Modal
          className="fh-agreement-consent-blocking"
          title="用户协议"
          visible={blockingVisible}
          closable={false}
          maskClosable={false}
          keyboard={false}
          width={560}
          footer={[
            <Button
              key="confirm"
              type="primary"
              disabled={!accepted}
              loading={submitting}
              onClick={this.handleBlockingConfirm}
            >
              同意并继续
            </Button>
          ]}
        >
          <Checkbox
            className="fh-agreement-consent-checkbox"
            checked={accepted}
            onChange={(e) => this.setState({ accepted: e.target.checked })}
          >
            <span className="fh-agreement-consent-text">我已阅读并同意</span>
            {this.renderAgreementLinks()}
          </Checkbox>
        </Modal>
        {this.renderDetailModal()}
      </React.Fragment>
    );
  }

  render() {
    return this.props.mode === 'blocking' ? this.renderBlockingMode() : this.renderFormMode();
  }
}

export default FhAgreementConsent;
